import { CONTENT_BACKOFFICE } from '@/services/axios.config'
import {
  adaptPatchCertificationVersionToApi,
  adaptNewCertificationVersionToApi,
  adaptCertificationVersionToStore,
  adaptCertificationVersionsListToStore,
} from './adapters/certificationAdapter'
import { buildServices } from './utils/build_services'
import buildParamList from './utils/build_params'
import isEmpty from 'lodash.isempty'
import omitBy from 'lodash.omitby'

const services = [
  {
    name: 'listCertificationVersions',
    method: 'get',
    url: (params) =>
      `/certification-versions/summary${buildParamList(
        omitBy(params, isEmpty),
        true
      )}`,
  },
  {
    name: 'getCertificationHistory',
    method: 'get',
    url: (params) => {
      const query = {
        include_all: params.includeAll ? 'true' : null,
        response_format: 'envelope',
        page_size: 'none',
      }
      return `/certifications/${params.certificationId}/versions-summary${buildParamList(
        omitBy(query, isEmpty),
        true
      )}`
    },
    resolver: adaptCertificationVersionsListToStore,
  },
  {
    name: 'getCertificationChangeNotes',
    method: 'get',
    url: (params) => {
      const query = {
        scope_entity_type: 'certification',
        scope_entity_id: params.certificationId,
        entity_type: params.entityType || null,
        entity_id: params.entityId || null,
        entity_ids: (params.entityIds || []).filter(Boolean).join(','),
        // Strings: lodash isEmpty treats numbers as empty and would drop them.
        page_size: String(params.pageSize || 50),
        page: params.page ? String(params.page) : null,
      }

      return `/change-notes${buildParamList(omitBy(query, isEmpty), true)}`
    },
  },
  {
    name: 'createCertificationExam',
    method: 'post',
    url: () => `/certification-exams`,
    data: (params) => ({
      certification_version_id: params.sourceVersionId,
    }),
  },
  {
    name: 'createCertificationVersion',
    method: 'post',
    url: () => `/certification-versions`,
    data: (params) => adaptNewCertificationVersionToApi(params),
    resolver: adaptCertificationVersionToStore,
  },
  {
    name: 'createCertificationOrchestrator',
    method: 'post',
    url: () => '/certification-orchestrator',
    data: (payload) => payload,
  },
  {
    name: 'updateCertificationVersion',
    method: 'patch',
    url: (params) => `/certification-versions/${params.certificationVersionId}`,
    data: (params) => adaptPatchCertificationVersionToApi(params),
    resolver: adaptCertificationVersionToStore,
  },
  {
    name: 'updateCertification',
    method: 'patch',
    url: (params) => `/certifications/${params.certificationId}`,
    data: (params) => params.payload,
  },
  {
    name: 'uploadCertificationBadge',
    method: 'post',
    url: (params) => `/certifications/${params.certificationId}/badge`,
    data: (params) => {
      const formData = new FormData()
      formData.append('file', params.file)
      if (params.changeNote) formData.append('change_note', params.changeNote)
      return formData
    },
  },
  {
    name: 'syncCertificationVersionStatus',
    method: 'post',
    url: (params) =>
      `/certification-versions/${params.certificationVersionId}/status-sync`,
    data: (params) => {
      const payload = {
        target_status: params.status,
        exam_id: params.examId,
      }

      if (params.changeNote) {
        payload.change_note = params.changeNote
      }

      return payload
    },
  },
  {
    name: 'getExamByCertificationVersion',
    method: 'get',
    url: (params) =>
      `/certification-exams?certification_version_id=${params.certificationVersionId}`,
  },
  {
    name: 'getCertificationVersion',
    method: 'get',
    url: (params) => `/certification-versions/${params.certificationVersionId}`,
    resolver: adaptCertificationVersionToStore,
  },
  {
    name: 'getCertificationVersionWithCertification',
    method: 'get',
    url: (params) =>
      `/certification-versions/${params.certificationVersionId}?include_certification=true`,
    resolver: adaptCertificationVersionToStore,
  },
  {
    name: 'getCertificationExam',
    method: 'get',
    url: (params) => `/certification-exams/${params.examId}`,
  },
  {
    name: 'updateCertificationExam',
    method: 'patch',
    url: (params) => `/certification-exams/${params.examId}`,
    data: (params) => params.payload,
  },
  {
    name: 'uploadCertificationExamCsv',
    method: 'post',
    url: () => '/certification-exams/upload',
    data: (params) => params.formData,
  },
  {
    name: 'validateCertificationExamCsv',
    method: 'post',
    url: () => '/certification-exams/exam-csv-validations',
    data: (params) => params.formData,
  },
  {
    name: 'downloadCertificationExamCsv',
    method: 'get',
    url: (params) => `/certification-exams/${params.examId}/download-csv`,
  },
  {
    name: 'downloadCertificationExamTemplateCsv',
    method: 'post',
    url: () => '/certification-exams/templates/download',
    data: (params) => params.payload,
  },
  {
    name: 'getLaunchConfiguration',
    method: 'get',
    url: (params) =>
      `/certification-versions/${params.certificationVersionId}/launch-configuration`,
  },
  {
    name: 'updateLaunchConfiguration',
    method: 'patch',
    url: (params) =>
      `/certification-versions/${params.certificationVersionId}/launch-configuration`,
    data: (params) => params.payload,
  },
  {
    name: 'updateLaunchSellability',
    method: 'put',
    url: (params) =>
      `/certification-versions/${params.certificationVersionId}/launch-configuration/sellability`,
    data: (params) => params.payload,
  },
]

export const {
  listCertificationVersions,
  getCertificationHistory,
  getCertificationChangeNotes,
  createCertificationExam,
  createCertificationVersion,
  createCertificationOrchestrator,
  updateCertificationVersion,
  updateCertification,
  uploadCertificationBadge,
  syncCertificationVersionStatus,
  getCertificationVersionWithCertification,
  getCertificationVersion,
  getExamByCertificationVersion,
  getCertificationExam,
  updateCertificationExam,
  uploadCertificationExamCsv,
  validateCertificationExamCsv,
  downloadCertificationExamCsv,
  downloadCertificationExamTemplateCsv,
  getLaunchConfiguration,
  updateLaunchConfiguration,
  updateLaunchSellability,
} = buildServices(CONTENT_BACKOFFICE, services)

export const listCertificationExams = getExamByCertificationVersion
